"use client";

import { Button } from "@bice_vida/ds-components-button";
import { StepProgressBar } from "@bice_vida/ds-components-step-progress-bar";
import {
  StepProgressProvider,
  useStepProgressState,
} from "@front-account/components/common/action-buttons/step-progress-context";
import { useRecoveryFlowConfig } from "@front-account/journeys/recovery/use-recovery-flow-config";
import { env } from "@front-account/utils/libs/env";
import { IconButton } from "@front-login/components/common/icon-button";
import { OTP_TOTAL_STEPS } from "@login/constants/recovery-account";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { MdArrowBack } from "react-icons/md";

import styles from "./recovery.module.scss";

function RecoverPasswordStepProgressBar({ hasBack }: { hasBack: boolean }) {
  const t = useTranslations("recovery-account");
  const router = useRouter();
  const { onContinue, disabled, loading } = useStepProgressState();
  const { useNewRecoveryFlow } = useRecoveryFlowConfig();
  const currentStep = 1;

  return (
    <div className={styles.stepProgressBarWrapper}>
      <StepProgressBar
        containerClassName={styles.stepProgressBar}
        currentStep={currentStep}
        steps={OTP_TOTAL_STEPS}
        subtitle={useNewRecoveryFlow ? t("STEPS_LEFT", { count: OTP_TOTAL_STEPS - currentStep + 1 }) : undefined}
        textOnBackButton={t("BACK")}
        textOnContinueButton={t("CONTINUE")}
        onBack={() => {
          if (hasBack) router.back();
        }}
        onContinue={onContinue}
        disabled={disabled ?? !onContinue}
        loading={loading ?? false}
      />

      <Link
        href={hasBack ? "#" : env.NEXT_PUBLIC_LOGIN_URL}
        className={styles.backButtonOverlay}
        onClick={() => {
          if (hasBack) router.back();
        }}
      >
        <Button size="l" variant="link">
          {t("BACK")}
        </Button>
      </Link>
    </div>
  );
}

export default function RecoveryLayoutClient({
  children,
  hasBack,
}: {
  children: React.ReactNode;
  hasBack: boolean;
}) {
  const t = useTranslations("recovery-account");
  const router = useRouter();

  useRecoveryFlowConfig();

  return (
    <StepProgressProvider>
      <div className={styles.container}>
        <Link
          href={hasBack ? "#" : env.NEXT_PUBLIC_LOGIN_URL}
          className={styles.topBackButton}
          onClick={(e) => {
            if (hasBack) {
              e.preventDefault();
              router.back();
            }
          }}
          aria-label={t("BACK")}
        >
          <IconButton
            aria-label={t("BACK")}
            className={styles.topBackButtonIcon}
            icon={<MdArrowBack />}
            tabIndex={-1}
            type="button"
          />

          <span>{t("BACK")}</span>
        </Link>

        {children}

        <RecoverPasswordStepProgressBar hasBack={hasBack} />
      </div>
    </StepProgressProvider>
  );
}
